import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { CompensationService } from '@/services/compensationService'
import { toast } from 'sonner'
import {
  CompensationType,
  CompensationApplicationTarget,
  CompensationCategory,
  Compensation,
  COMPENSATION_APPLICATION_TARGETS,
  getCompensationCategoriesByType,
  CreateCompensationData,
  UpdateCompensationData
} from '@/types/compensation'
import { DriverMaster } from '@/types/driverMaster'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import { Loader2 } from 'lucide-react'
import { getPeriodOptions } from '@/utils/periodHelpers'

interface AddSingleCompensationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  compensation?: Compensation | null  // If provided, dialog is in edit mode
  prefilledDriverId?: string          // For create mode only
  prefilledPeriod?: string            // For create mode only
  drivers: DriverMaster[]
  onSuccess?: () => void
}

export function AddSingleCompensationDialog({
  open,
  onOpenChange,
  compensation,
  prefilledDriverId,
  prefilledPeriod,
  drivers,
  onSuccess
}: AddSingleCompensationDialogProps) {
  const { currentUser: user } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Determine if we're in edit mode
  const isEditMode = !!compensation

  // Form state
  const [driverId, setDriverId] = useState<string>('')
  const [period, setPeriod] = useState<string>('')
  const [type, setType] = useState<CompensationType>('compensation')
  const [applyTo, setApplyTo] = useState<CompensationApplicationTarget>('net_earnings')
  const [amount, setAmount] = useState<string>('')
  const [category, setCategory] = useState<CompensationCategory | ''>('')
  const [comment, setComment] = useState<string>('')

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (compensation) {
        // Edit mode: populate from existing compensation
        setDriverId(compensation.driverId)
        setPeriod(compensation.period)
        setType(compensation.type)
        setApplyTo(compensation.applyTo)
        setAmount(compensation.amount.toString())
        setCategory(compensation.category)
        setComment(compensation.comment || '')
      } else {
        // Create mode: use prefilled values
        setDriverId(prefilledDriverId || '')
        setPeriod(prefilledPeriod || '')
        setType('compensation')
        setApplyTo('net_earnings')
        setAmount('')
        setCategory('')
        setComment('')
      }
    } else {
      // Reset form when dialog closes
      setDriverId('')
      setPeriod('')
      setType('compensation')
      setApplyTo('net_earnings')
      setAmount('')
      setCategory('')
      setComment('')
    }
  }, [open, compensation, prefilledDriverId, prefilledPeriod])

  // Get driver options for combobox
  const driverOptions: ComboboxOption[] = drivers.map(driver => ({
    value: driver.id,
    label: driver.primaryName
  }))

  // Get category options filtered by type
  const categoryOptions: ComboboxOption[] = getCompensationCategoriesByType(type).map(cat => ({
    value: cat.value,
    label: cat.label
  }))

  // Reset category when type changes
  useEffect(() => {
    const categoriesForType = getCompensationCategoriesByType(type)
    const currentCategoryValid = categoriesForType.some(cat => cat.value === category)
    if (!currentCategoryValid) {
      setCategory('')
    }
  }, [type, category])

  // Validation
  const canSubmit = () => {
    if (!driverId || !period || !amount || !category) {
      return false
    }

    const amountNum = parseFloat(amount)
    if (isNaN(amountNum) || amountNum <= 0) {
      return false
    }

    return true
  }

  // Handle submit
  const handleSubmit = async () => {
    if (!user || !canSubmit()) return

    setIsSubmitting(true)

    try {
      const amountNum = parseFloat(amount)

      if (isEditMode && compensation) {
        // Edit mode: update existing compensation
        const updateData: UpdateCompensationData = {
          type,
          applyTo,
          amount: amountNum,
          category: category as CompensationCategory,
          comment: comment.trim() || undefined
        }

        await CompensationService.updateCompensation(user, compensation.id, updateData)

        toast.success(
          type === 'compensation'
            ? 'Ersättning har uppdaterats'
            : 'Avdrag har uppdaterats'
        )
      } else {
        // Create mode: create new compensation
        const selectedDriver = drivers.find(d => d.id === driverId)
        if (!selectedDriver) {
          toast.error('Kunde inte hitta vald förare')
          setIsSubmitting(false)
          return
        }

        const createData: CreateCompensationData = {
          driverId: selectedDriver.id,
          period,
          type,
          applyTo,
          amount: amountNum,
          category: category as CompensationCategory,
          comment: comment.trim() || undefined
        }

        await CompensationService.createCompensation(user, createData)

        toast.success(
          type === 'compensation'
            ? 'Ersättning har lagts till'
            : 'Avdrag har lagts till'
        )
      }

      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      console.error('Failed to save compensation:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : isEditMode
            ? 'Kunde inte uppdatera tillägg/avdrag'
            : 'Kunde inte lägga till tillägg/avdrag'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Redigera' : 'Lägg till'} Tillägg/Avdrag</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Driver Selection */}
          <div className="space-y-2">
            <Label htmlFor="driver">Förare *</Label>
            <Combobox
              options={driverOptions}
              value={driverId}
              onValueChange={setDriverId}
              placeholder="Välj förare..."
              searchPlaceholder="Sök förare..."
              emptyMessage="Ingen förare hittades."
              disabled={isEditMode || !!prefilledDriverId}
            />
          </div>

          {/* Period Selection */}
          <div className="space-y-2">
            <Label htmlFor="period">Period *</Label>
            <Combobox
              options={getPeriodOptions()}
              value={period}
              onValueChange={setPeriod}
              placeholder="Välj period..."
              searchPlaceholder="Sök period..."
              emptyMessage="Ingen period hittades."
              disabled={isEditMode || !!prefilledPeriod}
            />
          </div>

          {/* Type Selection */}
          <div className="space-y-2">
            <Label>Typ *</Label>
            <RadioGroup value={type} onValueChange={(value) => setType(value as CompensationType)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="compensation" id="compensation" />
                <Label htmlFor="compensation" className="font-normal cursor-pointer">
                  Ersättning (lägger till belopp)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="deduction" id="deduction" />
                <Label htmlFor="deduction" className="font-normal cursor-pointer">
                  Avdrag (drar av belopp)
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Application Target Selection */}
          <div className="space-y-2">
            <Label>Applicera på *</Label>
            <RadioGroup value={applyTo} onValueChange={(value) => setApplyTo(value as CompensationApplicationTarget)}>
              {COMPENSATION_APPLICATION_TARGETS.map(target => (
                <div key={target.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={target.value} id={target.value} />
                  <Label htmlFor={target.value} className="font-normal cursor-pointer">
                    {target.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="amount">Belopp (SEK) *</Label>
            <Input
              id="amount"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
            />
          </div>

          {/* Category Selection */}
          <div className="space-y-2">
            <Label htmlFor="category">Kategori *</Label>
            <Combobox
              options={categoryOptions}
              value={category}
              onValueChange={(value) => setCategory(value as CompensationCategory)}
              placeholder="Välj kategori..."
              searchPlaceholder="Sök kategori..."
              emptyMessage="Ingen kategori hittades."
            />
          </div>

          {/* Comment Input */}
          <div className="space-y-2">
            <Label htmlFor="comment">Kommentar (valfri)</Label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Lägg till en kommentar..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Avbryt
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit() || isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Spara
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
